/*
requestSharer.js — every REQUEST-SCOPED licensee (sharer) call in one place (XOL-626).

RTS step 3 links the sharer a request created to that request (`licensees.action_id`, set
by the API). Everything the step does with it goes through a path built here, so a route
that changes is a one-line change.

The id in these paths is the REQUEST (action) id, never the licensee id.

Paths are relative, with no leading `/api`: callers wrap them in `getEndpoint(path)` (or
`getEndpoint(path, id)` through `saveForm`).

  BUILT (xolas-tms-api PR #285 @ c7b2b3d8, contract §11):
    GET  licensees/action/{actionId}               the request's linked sharer (+ `existing`)
    PUT  licensees/action/{actionId}               modify it (create's gate)
    GET  licensees/action/{actionId}/rate          the rate read (any Tower Co user; the request's
                                                   telco only once the Tower Co has shared it)
    PUT  licensees/action/{actionId}/rate-share    the per-request share switch ({actionStepId, shared})
    GET  licensees/action/{actionId}/rate-options  the rates a Create may offer (D19), by-org paginator
    PUT  licensees/action/{actionId}/link          link the telco's unlinked sharer at the site to this
                                                   request (D26 / S20, the Conflict note's "Link" button)

  Before the API deploys, EVERY request-scoped route above answers a bare 404 (`isRouteMissing`).
  Only the rate read checks for it (step3.vue `onRateReadFailed`: no switch, no cards); every other
  call treats a bare 404 as a failure like any other.

ES5, no optional chaining or nullish coalescing (Babel 6).
------------------------------------------------------------------------ */

// 'licensees/action' is also the `saveForm` base: saveForm(this, '/' + LINKED_SHARER_BASE, actionId, ...)
// builds PUT /api/licensees/action/{actionId}.
export var LINKED_SHARER_BASE = 'licensees/action'

export function linkedSharerPath (actionId) {
  return LINKED_SHARER_BASE + '/' + actionId
}

export function rateReadPath (actionId) {
  return LINKED_SHARER_BASE + '/' + actionId + '/rate'
}

export function rateShareWritePath (actionId) {
  return LINKED_SHARER_BASE + '/' + actionId + '/rate-share'
}

export function rateOptionsPath (actionId) {
  return LINKED_SHARER_BASE + '/' + actionId + '/rate-options'
}

export function linkPath (actionId) {
  return LINKED_SHARER_BASE + '/' + actionId + '/link'
}

// The link body is exactly these two keys (any other key is a 422 `read_only_fields`).
export function linkBody (actionStepId, licenseeId) {
  return { actionStepId: actionStepId, licensee_id: licenseeId }
}

// `shared` must be a JSON boolean (the API refuses "true", 1 and 0); any other key is a 422
// `read_only_fields`.
export function rateShareBody (shared, actionStepId) {
  return { shared: !!shared, actionStepId: actionStepId }
}

// Machine codes of a refusal body `{code, message, error}` (the XOL-595 shape).
export var RATE_NOT_SHARED = 'rate_not_shared'
export var NOT_LINKED = 'not_linked'
export var NOT_FOUND = 'not_found'
export var ALREADY_LINKED = 'already_linked'
export var LINKED_ELSEWHERE = 'linked_elsewhere'
export var SHARER_NOT_ACTIVE = 'sharer_not_active'
export var SHARER_TERMINATED = 'sharer_terminated'
export var TELCO_SITE_ID_IN_USE = 'telco_site_id_in_use'
export var SHARER_NO_FORBIDDEN = 'sharer_no_forbidden'
export var READ_ONLY_FIELDS = 'read_only_fields'
export var INVALID_VALUE = 'invalid_value'

// The machine code of a failed axios call, or null.
export function errorCode (err) {
  var data = err && err.response && err.response.data

  return (data && typeof data === 'object' && typeof data.code === 'string') ? data.code : null
}

// The human sentence of a refusal (`message`, else `error`), or null.
export function errorMessage (err) {
  var data = err && err.response && err.response.data

  if (data && typeof data === 'object') {
    if (typeof data.message === 'string' && data.message) return data.message
    if (typeof data.error === 'string' && data.error) return data.error
  }

  return null
}

// A 404 with NO `code`: what a bare Laravel "route not found" looks like before an API
// route ships. The built routes answer a CODED 404 (`not_linked`, `not_found`).
export function isRouteMissing (err) {
  var response = err && err.response

  return !!response && response.status === 404 && errorCode(err) === null
}

function numberOrNull (v) {
  if (v === undefined || v === null || v === '') return null

  var n = Number(v)

  return isNaN(n) ? null : n
}

// The rate read (contract §11.7, also the body of a successful rate-share PUT), normalised:
//   { shared, sharedBy, sharedAt, rate: {id, name}|null, monthlyFee: number|null,
//     version: object|null, position: {billingMode, sharerNo, siteSharerQty,
//     applicablePosition, currentTierIndex} }
// Tolerates missing keys. `version` is passed through as it came ({id, version_no, is_current,
// status, published_at, name, fromSharer, toSharer, tiers[]}); `version.status` is the RATE's status,
// which rate-tiers-cell needs (it greys the table unless it is 'Active'). `position.currentTierIndex`
// is a tier `seqNo`, not an array index; with `applicablePosition` it picks the highlighted cell (D24).
export function normaliseRateRead (body) {
  var b = (body && typeof body === 'object') ? body : {}
  var rate = (b.rate && typeof b.rate === 'object') ? { id: b.rate.id, name: b.rate.name } : null
  var p = (b.position && typeof b.position === 'object') ? b.position : {}

  return {
    shared: !!b.shared,
    sharedBy: b.sharedBy || null,
    sharedAt: b.sharedAt || null,
    rate: rate,
    monthlyFee: numberOrNull(b.monthlyFee),
    version: (b.version && typeof b.version === 'object') ? b.version : null,
    position: {
      billingMode: p.billingMode || null,
      sharerNo: numberOrNull(p.sharerNo),
      siteSharerQty: numberOrNull(p.siteSharerQty),
      applicablePosition: numberOrNull(p.applicablePosition),
      currentTierIndex: numberOrNull(p.currentTierIndex)
    }
  }
}
